import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Slider,
  Chip,
  Tabs,
  Tab,
  Tooltip,
  Paper,
  useTheme,
} from '@mui/material';
import {
  Speed,
  Tune,
  Bolt,
  CheckCircle,
} from '@mui/icons-material';

const SensitivityDemo: React.FC = () => {
  const theme = useTheme();

  // Slider State
  const [baseCapRate, setBaseCapRate] = useState<number>(4.75); // %
  const [rentGrowth, setRentGrowth] = useState<number>(3.0);   // % p.a.
  const [costInflation, setCostInflation] = useState<number>(4.0); // %
  const [ltv, setLtv] = useState<number>(60);                 // %
  const [metricMode, setMetricMode] = useState<'irr' | 'em' | 'gdv'>('irr');

  const capOffsets = [-0.5, -0.25, 0, 0.25, 0.5];
  const growthOffsets = [-2.0, -1.0, 0, 1.0, 2.0];

  const calcMatrixCell = (capDelta: number, growthDelta: number) => {
    const cap = baseCapRate + capDelta;
    const growth = rentGrowth + growthDelta;
    
    const capImpact = (4.75 - cap) * 8.5;
    const growthImpact = (growth - 3.0) * 1.6;
    const costImpact = (4.0 - costInflation) * 0.75;
    const ltvMultiplier = 1 + (ltv - 60) * 0.015;

    let irr = (16.5 + capImpact + growthImpact + costImpact) * ltvMultiplier;
    irr = Math.max(2.1, Math.min(38.5, irr));

    const em = 1 + (irr * 0.058);
    const baseGDV = 48.5;
    const gdv = baseGDV * (4.75 / cap) * (1 + (growth * 0.02));

    return { irr: irr.toFixed(1), em: em.toFixed(2), gdv: gdv.toFixed(1) };
  };

  const matrixData = useMemo(() => {
    return capOffsets.map((capDelta) => {
      const rowCap = (baseCapRate + capDelta).toFixed(2);
      const cells = growthOffsets.map((growthDelta) => {
        const rowGrowth = (rentGrowth + growthDelta).toFixed(1);
        const res = calcMatrixCell(capDelta, growthDelta);
        return {
          cap: parseFloat(rowCap),
          growth: parseFloat(rowGrowth),
          irr: parseFloat(res.irr),
          em: parseFloat(res.em),
          gdv: parseFloat(res.gdv),
        };
      });
      return { cap: parseFloat(rowCap), cells };
    });
  }, [baseCapRate, rentGrowth, costInflation, ltv]);

  // Light Mode Heatmap Cell Styling
  const getCellStyles = (irr: number) => {
    if (irr >= 20) return { bg: '#ecfdf5', border: '#a7f3d0', color: '#047857' }; // Target Emerald
    if (irr >= 16) return { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af' }; // Corporate Blue
    if (irr >= 12) return { bg: '#fffbeb', border: '#fde68a', color: '#b45309' }; // Amber
    return { bg: '#fef2f2', border: '#fecdd3', color: '#be123c' };                 // Rose Red
  };

  return (
    <Box id="sensitivity-section" sx={{ py: 12, bgcolor: '#f8fafc', position: 'relative', overflow: 'hidden' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 7 }}>
          <Chip
            icon={<Bolt sx={{ color: '#047857 !important' }} />}
            label="MULTIVARIABLE SENSITIVITY ENGINE"
            sx={{
              bgcolor: 'rgba(4, 120, 87, 0.08)',
              border: '1px solid rgba(4, 120, 87, 0.2)',
              color: '#047857',
              px: 1,
              py: 0.5,
              mb: 2,
              fontWeight: 700,
              fontSize: '0.75rem',
              letterSpacing: '0.05em',
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
              color: '#0f172a',
              fontSize: { xs: '2rem', md: '2.75rem' },
            }}
          >
            Interactive 1D & 2D Sensitivity Analysis
          </Typography>
          <Typography
            variant="body1"
            sx={{
              maxWidth: 720,
              mx: 'auto',
              color: '#475569',
              fontSize: { xs: '1rem', md: '1.15rem' },
            }}
          >
            Evaluate UK commercial capital stacks, yield compressions, and rental growth permutations across automated scenario matrices.
          </Typography>
        </Box>

        {/* Main Grid */}
        <Grid container spacing={4} alignItems="stretch">
          {/* Controls */}
          <Grid item xs={12} lg={4}>
            <Card
              sx={{
                p: 3.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justify: 'space-between',
                border: '1px solid #e2e8f0',
                background: '#ffffff',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Tune sx={{ color: '#047857' }} />
                  <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700 }}>
                    Model Parameter Controls
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>
                      Target Exit Yield / Cap Rate
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#047857', fontFamily: 'monospace', fontWeight: 700 }}>
                      {baseCapRate.toFixed(2)}%
                    </Typography>
                  </Box>
                  <Slider
                    value={baseCapRate}
                    min={3.5}
                    max={6.5}
                    step={0.05}
                    onChange={(_, v) => setBaseCapRate(v as number)}
                    sx={{ color: '#047857' }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>
                      Rental Growth (p.a.)
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1e40af', fontFamily: 'monospace', fontWeight: 700 }}>
                      {rentGrowth > 0 ? `+${rentGrowth.toFixed(1)}%` : `${rentGrowth.toFixed(1)}%`}
                    </Typography>
                  </Box>
                  <Slider
                    value={rentGrowth}
                    min={-2.0}
                    max={7.0}
                    step={0.25}
                    onChange={(_, v) => setRentGrowth(v as number)}
                    sx={{ color: '#1e40af' }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>
                      Capex & Cost Inflation
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#b45309', fontFamily: 'monospace', fontWeight: 700 }}>
                      {costInflation.toFixed(1)}%
                    </Typography>
                  </Box>
                  <Slider
                    value={costInflation}
                    min={0.0}
                    max={12.0}
                    step={0.5}
                    onChange={(_, v) => setCostInflation(v as number)}
                    sx={{ color: '#b45309' }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>
                      Senior Debt LTV Sizing
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6d28d9', fontFamily: 'monospace', fontWeight: 700 }}>
                      {ltv}% LTV
                    </Typography>
                  </Box>
                  <Slider
                    value={ltv}
                    min={40}
                    max={75}
                    step={5}
                    onChange={(_, v) => setLtv(v as number)}
                    sx={{ color: '#6d28d9' }}
                  />
                </Box>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: 'rgba(4, 120, 87, 0.05)',
                  border: '1px solid rgba(4, 120, 87, 0.2)',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Speed sx={{ color: '#047857', fontSize: 24 }} />
                <Box>
                  <Typography variant="caption" sx={{ color: '#047857', fontWeight: 700, display: 'block' }}>
                    2D MATRIX COMPUTATION
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#475569' }}>
                    25 sensitivity scenarios evaluated across yield & growth vectors
                  </Typography>
                </Box>
              </Paper>
            </Card>
          </Grid>

          {/* Matrix Visualization */}
          <Grid item xs={12} lg={8}>
            <Card
              sx={{
                p: { xs: 2.5, md: 3.5 },
                height: '100%',
                border: '1px solid #e2e8f0',
                background: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                justify: 'space-between',
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justify: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700 }}>
                    2D Heatmap Matrix (Exit Cap vs. Rent Growth)
                  </Typography>
                  <Tabs
                    value={metricMode}
                    onChange={(_, val) => setMetricMode(val)}
                    sx={{
                      minHeight: 36,
                      '& .MuiTabs-indicator': { backgroundColor: '#047857' },
                    }}
                  >
                    <Tab label="Levered IRR (%)" value="irr" sx={{ minHeight: 36, py: 0.5, fontSize: '0.8rem', color: '#64748b', '&.Mui-selected': { color: '#047857', fontWeight: 700 } }} />
                    <Tab label="Equity Multiple (x)" value="em" sx={{ minHeight: 36, py: 0.5, fontSize: '0.8rem', color: '#64748b', '&.Mui-selected': { color: '#047857', fontWeight: 700 } }} />
                    <Tab label="Exit GDV (£M)" value="gdv" sx={{ minHeight: 36, py: 0.5, fontSize: '0.8rem', color: '#64748b', '&.Mui-selected': { color: '#047857', fontWeight: 700 } }} />
                  </Tabs>
                </Box>

                <Box sx={{ overflowX: 'auto' }}>
                  <Box sx={{ minWidth: 500 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '110px repeat(5, 1fr)', gap: 1, mb: 1, textAlign: 'center' }}>
                      <Box sx={{ p: 1, bgcolor: 'transparent', textAlign: 'left' }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                          Cap \ Rent
                        </Typography>
                      </Box>
                      {growthOffsets.map((gOff, idx) => {
                        const gVal = (rentGrowth + gOff).toFixed(1);
                        const isBase = gOff === 0;
                        return (
                          <Box
                            key={idx}
                            sx={{
                              p: 1,
                              bgcolor: isBase ? 'rgba(30, 64, 175, 0.08)' : '#f8fafc',
                              borderRadius: 1.5,
                              border: isBase ? '1px solid rgba(30, 64, 175, 0.3)' : '1px solid transparent',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: isBase ? '#1e40af' : '#64748b', fontWeight: 700, fontFamily: 'monospace' }}>
                              {parseFloat(gVal) > 0 ? `+${gVal}%` : `${gVal}%`}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>

                    {matrixData.map((row, rIdx) => {
                      const isBaseCap = capOffsets[rIdx] === 0;
                      return (
                        <Box
                          key={rIdx}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '110px repeat(5, 1fr)',
                            gap: 1,
                            mb: 1,
                            alignItems: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              p: 1,
                              bgcolor: isBaseCap ? 'rgba(4, 120, 87, 0.08)' : '#f8fafc',
                              borderRadius: 1.5,
                              border: isBaseCap ? '1px solid rgba(4, 120, 87, 0.3)' : '1px solid transparent',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: isBaseCap ? '#047857' : '#0f172a', fontWeight: 700, fontFamily: 'monospace' }}>
                              {row.cap.toFixed(2)}% Exit
                            </Typography>
                          </Box>

                          {row.cells.map((cell, cIdx) => {
                            const val = metricMode === 'irr' ? cell.irr : metricMode === 'em' ? cell.em : cell.gdv;
                            const unit = metricMode === 'irr' ? '%' : metricMode === 'em' ? 'x' : 'M';
                            const prefix = metricMode === 'gdv' ? '£' : '';
                            const cellStyle = getCellStyles(cell.irr);
                            const isBaseCell = capOffsets[rIdx] === 0 && growthOffsets[cIdx] === 0;

                            return (
                              <Tooltip
                                key={cIdx}
                                title={`Cap: ${cell.cap}% | Rent Growth: ${cell.growth}% | IRR: ${cell.irr}% | GDV: £${cell.gdv}M`}
                                arrow
                              >
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 1.5,
                                    bgcolor: cellStyle.bg,
                                    borderRadius: 2,
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    border: isBaseCell ? '2px solid #047857' : `1px solid ${cellStyle.border}`,
                                    transition: 'all 0.2s ease',
                                    boxShadow: isBaseCell ? '0 0 10px rgba(4, 120, 87, 0.2)' : 'none',
                                    '&:hover': {
                                      transform: 'scale(1.05)',
                                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                      zIndex: 2,
                                    },
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: cellStyle.color,
                                      fontWeight: 800,
                                      fontFamily: '"JetBrains Mono", monospace',
                                      fontSize: { xs: '0.85rem', md: '0.95rem' },
                                    }}
                                  >
                                    {prefix}{val}{unit}
                                  </Typography>
                                </Paper>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  mt: 3,
                  pt: 2,
                  borderTop: '1px solid #f1f5f9',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justify: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                    IRR Return Bands:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#047857' }} />
                    <Typography variant="caption" sx={{ color: '#64748b' }}>&gt;20% Target</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#1e40af' }} />
                    <Typography variant="caption" sx={{ color: '#64748b' }}>16-20% Core+</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#b45309' }} />
                    <Typography variant="caption" sx={{ color: '#64748b' }}>12-16% Moderate</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#be123c' }} />
                    <Typography variant="caption" sx={{ color: '#64748b' }}>&lt;12% Low</Typography>
                  </Box>
                </Box>

                <Chip
                  icon={<CheckCircle sx={{ color: '#047857 !important', fontSize: '16px !important' }} />}
                  label="UK Stamp Duty (SDLT) Included"
                  size="small"
                  sx={{
                    bgcolor: '#f1f5f9',
                    color: '#475569',
                    fontSize: '0.75rem',
                  }}
                />
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default SensitivityDemo;
